package com.dotdrives.web.pages.settings

import com.dotdrives.controller.ConfigurationController
import com.dotdrives.core.uuidV7
import com.dotdrives.design.DotButtonVariant
import kotlinx.browser.document
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.form
import com.dotdrives.model.*
import com.dotdrives.model.Target
import com.dotdrives.repository.SupabaseConfigurationRepository
import com.dotdrives.web.ui.*
import kotlinx.coroutines.*
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import web.cssom.ClassName
import web.dom.ElementId
import web.html.InputType
import web.html.checkbox
import web.html.date
import web.html.submit

external interface ConfigurationPanelProps : Props {
    var orgId: String
    var role: String
    var process: Boolean
}

val ConfigurationPanel =
    FC<ConfigurationPanelProps> { props ->
        val (holder) =
            useState {
                val scope = MainScope()
                scope to
                    ConfigurationController(
                        SupabaseConfigurationRepository(),
                        scope,
                        props.orgId,
                        props.role,
                    )
            }
        val c = holder.second
        val state = useFlowState(c.state)
        useEffect(holder) {
            c.load()
            try {
                awaitCancellation()
            } finally {
                holder.first.cancel()
            }
        }
        val (target, setTarget) = useState<Target?>(null)
        val (name, setName) = useState("")
        val (amount, setAmount) = useState("")
        val (start, setStart) = useState("")
        val (end, setEnd) = useState("")
        val (active, setActive) = useState(true)
        val (editorOpen, setEditorOpen) = useState(false)
        val (draftId, setDraftId) = useState("")
        val (submitted, setSubmitted) = useState(false)
        val (savedName, setSavedName) = useState("")
        val (savedId, setSavedId) = useState("")
        fun closeEditor() { setEditorOpen(false); setSubmitted(false); c.clearError() }
        fun openEditor(t: Target?) {
            c.clearError(); setTarget(t); setDraftId(t?.id ?: uuidV7())
            setName(t?.name.orEmpty()); setAmount(t?.goalAmount?.toString().orEmpty())
            setStart(t?.goalStartsOn.orEmpty()); setEnd(t?.goalEndsOn.orEmpty())
            setActive(t?.isActive ?: true); setSavedName(""); setEditorOpen(true)
        }
        useEffect(editorOpen) {
            document.getElementById(if (editorOpen) "settings-target-name" else "settings-target-new")?.asDynamic()?.focus()
        }
        useEffect(state.saving, state.saved, state.error) {
            if (submitted && !state.saving) {
                if (state.saved && state.error == null) {
                    setSavedName(name.trim()); setSavedId(draftId); setEditorOpen(false)
                }
                setSubmitted(false)
            }
        }
        useEffect(savedId, state.snapshot) {
            if (savedId.isNotBlank() && !editorOpen) document.getElementById("target-$savedId")?.asDynamic()?.focus()
        }
        if (props.process) {
            ProcessConfigurationPanel { controller = c; this.state = state; role = props.role }
        } else div {
            className = ClassName("settings-section-card settings-targets")
            div { className = ClassName("settings-section-title"); +"Campaign goals" }
            div {
                className = ClassName("settings-section-subtitle")
                +"Name campaign goals for drives. An amount and dates are optional; inactive goals remain visible for history."
            }
            state.error?.let { DotBanner { message = it; onRetry = if (editorOpen) null else c::load } }
            state.refreshError?.let { DotBanner { message = it; onRetry = c::load } }
            if (state.loading) div { +"Refreshing targets…" }
            if (savedName.isNotBlank()) div { asDynamic()["role"] = "status"; className = ClassName("settings-saved"); +"Saved target “$savedName”." }
            if (editorOpen) form {
                className = ClassName("settings-focused-form")
                onKeyDown = { if (it.key == "Escape" && !state.saving) closeEditor() }
                onSubmit = {
                    it.preventDefault()
                    if (!state.saving) { setSubmitted(true); c.saveTarget(draftId, name, amount, start, end, active) }
                }
                div { className = ClassName("settings-section-title"); +if (target == null) "New target" else "Edit target" }
                label {
                    +"Target name"
                    input { id = "settings-target-name".unsafeCast<ElementId>(); value = name; required = true; disabled = state.saving; onChange = { setName(it.currentTarget.value) } }
                }
                label {
                    +"Goal amount (USD, optional)"
                    input { value = amount; disabled = state.saving; asDynamic()["inputMode"] = "decimal"; placeholder = "0.00"; onChange = { setAmount(it.currentTarget.value) } }
                }
                div {
                    className = ClassName("settings-form-grid")
                    label { +"Start date"; input { type = InputType.date; value = start; disabled = state.saving; onChange = { setStart(it.currentTarget.value) } } }
                    label { +"End date"; input { type = InputType.date; value = end; disabled = state.saving; onChange = { setEnd(it.currentTarget.value) } } }
                }
                div { className = ClassName("settings-section-subtitle"); +"Set both dates to track progress over time, or leave both blank. Start and end dates are included (UTC)." }
                label { input { type = InputType.checkbox; checked = active; disabled = state.saving; onChange = { setActive(it.currentTarget.checked) } }; +"Active target" }
                div {
                    className = ClassName("settings-editor-actions")
                    DotButton { text = "Cancel"; variant = DotButtonVariant.Secondary; enabled = !state.saving; onClick = { closeEditor() } }
                    button { type = web.html.ButtonType.submit; className = ClassName("dot-button dot-button-primary"); disabled = state.saving; +if (state.saving) "Saving…" else "Save target" }
                }
            } else {
                if (canAdminister(props.role)) button {
                    id = "settings-target-new".unsafeCast<ElementId>(); className = ClassName("dot-button dot-button-primary")
                    onClick = { openEditor(null) }; +"New campaign goal"
                }
                if (state.snapshot?.targets?.isEmpty() == true) div { className = ClassName("settings-section-subtitle"); +"No targets yet. Add one to track your fundraising progress." }
                state.snapshot?.targets?.forEach { t ->
                    div {
                        key = t.id.unsafeCast<Key>(); id = "target-${t.id}".unsafeCast<ElementId>(); tabIndex = -1
                        className = ClassName("settings-target-row" + if (savedId == t.id) " settings-item-saved" else "")
                        div {
                            className = ClassName("settings-target-copy")
                            div { className = ClassName("settings-target-name"); +t.name }
                            div { className = ClassName("settings-section-subtitle"); +(t.goalStartsOn?.let { "$it – ${t.goalEndsOn}" } ?: "No date period") }
                            GoalProgressLine { orgId = props.orgId; this.amount = t.goalAmount; this.start = t.goalStartsOn; this.end = t.goalEndsOn; kind = "campaign"; goalId = t.id }
                        }
                        div { className = ClassName("settings-status-pill"); +if (t.isActive) "Active" else "Inactive" }
                        div { className = ClassName("settings-target-amount"); +(t.goalAmount?.let { targetAmount(it) } ?: "No goal amount") }
                        if (canAdminister(props.role)) DotButton { text = "Edit target"; variant = DotButtonVariant.Secondary; onClick = { openEditor(t) } }
                    }
                }
            }
        }
    }

private fun targetAmount(amount: Double): String {
    val options = js("({style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2})")
    return (js("new Intl.NumberFormat('en-US', options)").format(amount) as String) + " USD"
}
